import { discountCents, grossCents, MAX_AMOUNT, toUnits, type DiscountType } from '@/lib/money';
import type { CatalogItem, EstimateLine, SaveLine } from '@/routes/estimates/useEstimates';

/** The three tax categories a line can carry (OD-19): labour, parts, or exempt wherever it is sold. */
export const TAX_CATEGORIES = ['Labor', 'Parts', 'Exempt'] as const;
export type TaxCategory = (typeof TAX_CATEGORIES)[number];

/**
 * One line as the builder holds it (M2-06): every number as the TEXT the
 * person typed, so "1." mid-keystroke is not rewritten to "1" under their
 * cursor, and so an over-precise entry can be refused rather than rounded.
 *
 * `key` is the builder's own identity for the row. The API regenerates line
 * ids on every save (lines are replaced as a whole), so a server id cannot
 * key a row that outlives the save it was read from.
 */
export interface DraftLine {
  key: string;
  description: string;
  quantity: string;
  unitPrice: string;
  taxCategory: TaxCategory;
  discountType: '' | DiscountType;
  discountValue: string;
  catalogItemId: string | null;
  /**
   * A line from an hourly labor item (M2-33): its quantity is hours and its
   * price a rate per hour. A typed labour row is told by its tax category
   * instead (`EstimateLineRow`), live as that changes.
   *
   * ⚠️ SENT BACK on every save (M2-33 follow-up). The API freezes the flag on
   * the line when it is written, and a whole-list save has no line identity,
   * so the flag a line was READ with is how an untouched line keeps it after
   * its item changes kind. A line added from the book carries the item's kind
   * now. The API takes the claim only when its item's kind or this document's
   * stored lines back it; a custom row is never hourly there.
   */
  hourly: boolean;
}

/** Which field is wrong, and the i18n key saying why. */
export type LineErrors = Partial<
  Record<'description' | 'quantity' | 'unitPrice' | 'discountValue', string>
>;

let sequence = 0;
const newKey = () => `line-${++sequence}`;

/** A number from the API (a `number | string`) as the text an input shows. */
function text(value: number | string | null): string {
  return value === null ? '' : String(Number(value));
}

export function draftFromLine(line: EstimateLine): DraftLine {
  return {
    key: newKey(),
    description: line.description,
    quantity: text(line.quantity),
    unitPrice: Number(line.unitPrice).toFixed(2),
    taxCategory: (TAX_CATEGORIES as readonly string[]).includes(line.taxCategory)
      ? (line.taxCategory as TaxCategory)
      : 'Parts',
    discountType:
      line.discountType === 'Percent' || line.discountType === 'Amount' ? line.discountType : '',
    discountValue: text(line.discountValue),
    // Kept, so the whole-list save does not quietly turn a catalog line custom.
    catalogItemId: line.catalogItemId,
    hourly: line.hourly,
  };
}

/**
 * A price-book item as a new line: name, price and tax category SNAPSHOTTED
 * (M2-04 — changing the book later must not change this estimate). The
 * category is the API's own mapping (`SalesTax.CategoryFor`): not taxable is
 * Exempt, otherwise a service is labour and a part is parts.
 */
export function draftFromCatalogItem(item: CatalogItem): DraftLine {
  return {
    key: newKey(),
    description: item.name,
    quantity: '1',
    unitPrice: Number(item.defaultPrice).toFixed(2),
    taxCategory: !item.taxable ? 'Exempt' : item.type === 'Part' ? 'Parts' : 'Labor',
    discountType: '',
    discountValue: '',
    catalogItemId: item.id,
    hourly: item.type === 'Labor',
  };
}

/**
 * A time-and-materials labour row (Kyle, 2026-09-26): a Labor line whose
 * quantity is hours and whose price is the hourly rate — pre-filled from the
 * technician's billable rate when the caller may see it, typed otherwise.
 */
export function labourLine(description: string, hourlyRate: number | null): DraftLine {
  return {
    key: newKey(),
    description,
    quantity: '1',
    unitPrice: hourlyRate === null ? '' : hourlyRate.toFixed(2),
    taxCategory: 'Labor',
    discountType: '',
    discountValue: '',
    catalogItemId: null,
    // Told by its Labor tax category, as it was before M2-33.
    hourly: false,
  };
}

export function customLine(): DraftLine {
  return {
    key: newKey(),
    description: '',
    quantity: '1',
    unitPrice: '',
    taxCategory: 'Parts',
    discountType: '',
    discountValue: '',
    catalogItemId: null,
    hourly: false,
  };
}

const MAX_UNITS_2 = toUnits(MAX_AMOUNT.toFixed(2), 2)!;
const MAX_UNITS_3 = toUnits(MAX_AMOUNT.toFixed(2), 3)!;

/**
 * The API's line rules, answered before the request: `SaveLineItemRequest`'s
 * bounds and `DocumentLines.Problem`'s discount rules. Null when the line is
 * fine. The server still enforces every one of them.
 */
export function validateLine(line: DraftLine): LineErrors | null {
  const errors: LineErrors = {};

  if (line.description.trim() === '') errors.description = 'estimates.line.errors.description';
  else if (line.description.length > 500)
    errors.description = 'estimates.line.errors.descriptionLength';

  const quantity = toUnits(line.quantity, 3);
  if (quantity === null) errors.quantity = 'estimates.line.errors.quantity';
  else if (quantity < 1n || quantity > MAX_UNITS_3)
    errors.quantity = 'estimates.line.errors.quantityRange';

  const unitPrice = toUnits(line.unitPrice, 2);
  if (unitPrice === null) errors.unitPrice = 'estimates.line.errors.unitPrice';
  else if (unitPrice > MAX_UNITS_2) errors.unitPrice = 'estimates.line.errors.unitPriceRange';

  if (line.discountType !== '') {
    const value = toUnits(line.discountValue, 2);
    if (value === null || value === 0n) errors.discountValue = 'estimates.line.errors.discount';
    else if (line.discountType === 'Percent' && value > 10_000n) {
      errors.discountValue = 'estimates.line.errors.discountPercent';
    } else if (
      line.discountType === 'Amount' &&
      quantity !== null &&
      unitPrice !== null &&
      value > grossCents(quantity, unitPrice)
    ) {
      errors.discountValue = 'estimates.line.errors.discountAmount';
    }
  }

  return Object.keys(errors).length === 0 ? null : errors;
}

/**
 * What a valid line sends. Numbers go as normalised decimal STRINGS, which
 * the API reads exactly (`AllowReadingFromString`) — a JSON number would pass
 * through a binary float on the way.
 */
export function toSaveLine(line: DraftLine): SaveLine {
  return {
    description: line.description.trim(),
    quantity: normalise(line.quantity),
    unitPrice: normalise(line.unitPrice),
    taxCategory: line.taxCategory,
    catalogItemId: line.catalogItemId,
    discountType: line.discountType === '' ? null : line.discountType,
    discountValue: line.discountType === '' ? null : normalise(line.discountValue),
    hourly: line.hourly,
  };
}

function normalise(value: string): string {
  const trimmed = value.trim();
  return trimmed.endsWith('.') ? trimmed.slice(0, -1) : trimmed;
}

/** A valid line's figures in cents, or null while it is not valid. */
export function lineCents(
  line: DraftLine,
): { gross: bigint; discount: bigint; total: bigint } | null {
  if (validateLine(line) !== null) return null;
  const gross = grossCents(toUnits(line.quantity, 3)!, toUnits(line.unitPrice, 2)!);
  const discount = discountCents(
    gross,
    line.discountType === '' ? null : line.discountType,
    line.discountType === '' ? null : toUnits(line.discountValue, 2),
  );
  return { gross, discount, total: gross - discount };
}

/** Moves the line at `from` by one place up (-1) or down (+1); unchanged at either end. */
export function moveLine(lines: DraftLine[], from: number, by: -1 | 1): DraftLine[] {
  const to = from + by;
  if (from < 0 || from >= lines.length || to < 0 || to >= lines.length) return lines;
  const next = [...lines];
  [next[from], next[to]] = [next[to]!, next[from]!];
  return next;
}
